'use client';

import Box from '@enlighten/design-system/dist/Box';
import Fade from '@enlighten/design-system/dist/Fade';
import Grid from '@enlighten/design-system/dist/Grid';
import List from '@enlighten/design-system/dist/List';
import ListItem from '@enlighten/design-system/dist/ListItem';
import ListItemButton from '@enlighten/design-system/dist/ListItemButton';
import type { Theme } from '@enlighten/design-system/dist/styles';
import Typography from '@enlighten/design-system/dist/Typography';
import useMediaQuery from '@enlighten/design-system/dist/useMediaQuery';
import Image from 'next/image';
import { useState } from 'react';

import TabImg1Pc from '@/assets/images/pages/remote-monitoring-control/section-2-tab-1.png';
import TabImg1Mobile from '@/assets/images/pages/remote-monitoring-control/section-2-tab-1-m.png';
import TabImg2Pc from '@/assets/images/pages/remote-monitoring-control/section-2-tab-2.png';
import TabImg2Mobile from '@/assets/images/pages/remote-monitoring-control/section-2-tab-2-m.png';
import TabImg3Pc from '@/assets/images/pages/remote-monitoring-control/section-2-tab-3.png';
import TabImg3Mobile from '@/assets/images/pages/remote-monitoring-control/section-2-tab-3-m.png';

const tabs = [
	{
		image: {
			desktop: TabImg1Pc,
			mobile: TabImg1Mobile,
		},
		label: '발전소 감시',
		value: 'monitoring',
		description: `발전왕 모니터링(RTU)을 통해 발전소를 원격에서 감시할 수 있어요.
출력, 발전 시간은 물론 인버터 별 데이터 등 다양한 데이터를 실시간으로 확인하세요.`,
	},
	{
		image: {
			desktop: TabImg2Pc,
			mobile: TabImg2Mobile,
		},
		label: '원격 제어',
		value: 'control',
		description: `이상 신호 발생 시, 안전관리자가 현장 방문 없이 원격으로 즉시 제어할 수 있어요.
인버터나 차단기를 원격으로 차단하여 각종 위험으로부터 더욱 안전한 관리가 가능해요.`,
	},
	{
		image: {
			desktop: TabImg3Pc,
			mobile: TabImg3Mobile,
		},
		label: '관리비 절감',
		value: 'reduce',
		description: `상주 안전관리자 고용에 대한 부담없이 원격 감시 제어 시스템으로
보다 합리적인 비용으로 발전소를 운영하세요.`,
	},
] as const;

export default function Section2() {
	const [tab, setTab] = useState<typeof tabs[number]['value']>('monitoring');

	const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));

	return (
		<Grid container justifyContent="center" bgcolor={{ xs: 'grey.200', md: 'transparent' }} width="100%" height={{ md: 800 }}>
			<Grid container direction={{ xs: 'column', md: 'row' }} width="100%" maxWidth={1440} padding={{ xs: '60px 0', md: 0 }}>
				<Grid container direction="column" justifyContent="center" flex={{ xs: 'none', md: 1 }} paddingX="20px">
					<Typography fontSize={{ xs: 24, md: 40 }} fontWeight={700}>
						{'현장부터 관제까지,\n모든 기능을 발전왕 비즈 하나로!'}
					</Typography>
					<Typography fontSize={{ xs: 14, md: 16 }} fontWeight={400} color="grey.A200" marginTop={{ xs: '10px', md: 1 }}>
						원격 감시부터 제어, 그리고 관리비 절감까지
					</Typography>
					<List
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-start',
							gap: '10px',
							fontSize: { xs: 13, md: 16 },
							fontWeight: 700,
							lineHeight: 1.5,
							color: 'grey.600',
							marginTop: 5,
							padding: 0,
							'.MuiListItem-root': {
								width: 'fit-content',
								padding: 0,
								'.MuiListItemButton-root': {
									bgcolor: 'common.white',
									borderWidth: 1,
									borderStyle: 'solid',
									borderColor: 'grey.300',
									borderRadius: 99,
									padding: { xs: '13px 18px', md: '13px 24px' },
									'&.active': {
										color: 'common.white',
										bgcolor: 'common.black',
										borderColor: 'common.black',
									},
								},
							},
						}}
					>
						{tabs.map(({ label, value }, i) => (
							<ListItem key={i}>
								<ListItemButton
									className={tab === value ? 'active' : undefined}
									sx={{
										fontSize: { xs: '12px', md: '16px' },
									}}
									onClick={() => {
										setTab(value as typeof tab);
									}}
								>
									{label}
								</ListItemButton>
							</ListItem>
						))}
					</List>
					<Grid position="relative" height={94}>
						{tabs.map(({ value, description }, i) => (
							<Fade key={i} in={tab === value}>
								<Box>
									<Typography
										position="absolute"
										fontSize={{ xs: 14, md: 16 }}
										lineHeight={1.5}
										color="grey.A400"
										marginTop="30px"
										marginBottom={{ xs: '14px', md: 2 }}
										whiteSpace={{ xs: 'normal', md: 'pre-line' }}
									>
										{description}
									</Typography>
								</Box>
							</Fade>
						))}
					</Grid>
				</Grid>
				<Grid position="relative" container flex={1} width="100%">
					<Grid
						position="relative"
						container
						justifyContent="center"
						alignItems="center"
						flex={1}
						width="100%"
						marginTop={{ xs: '20px', md: 0 }}
						sx={{
							aspectRatio: { xs: 4 / 3, md: 1 },
							'& img': {
								position: 'absolute',
								width: '100%',
								height: 'auto',
							},
						}}
					>
						{isMobile ? (
							<>
								{tabs.map(({ image: { mobile: image }, value }, i) => (
									<Fade key={i} in={tab === value}>
										<Image src={image} alt="" />
									</Fade>
								))}
							</>
						) : (
							<>
								{tabs.map(({ image: { desktop: image }, value }, i) => (
									<Fade key={i} in={tab === value}>
										<Image src={image} alt="" />
									</Fade>
								))}
							</>
						)}
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
}
